import React, { useState } from "react";
import PropTypes from "prop-types";
import { Box, Button as MuiButton, Typography } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { spacing } from "@mui/system";
import { InfoOutlined, Description } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { formatDate } from "../../utils/datetime";
import useScopedTranslation from "../../hooks/useScopedTranslation";
import GenericAvatar from "../../components/Icons/GenericAvatar";
import { Tooltip } from "../../components/Tooltip";
import ValidateOrderAdjustments from "./ValidateOrderAdjustments";
import {
  validateOrderStatuses,
  validateOrderTypes
} from "../../constants/index";
import { useDispatch } from "react-redux";
import {
  enqueueErrorSnackbar,
  enqueueSuccessSnackbar
} from "../../redux/actions/snackbarActions";
import { prependHttps } from "../../utils/string";

const Button = styled(MuiButton)(spacing);

const LastUpdatedLabel = dates => {
  const t = useScopedTranslation("validate.order.details");

  // Finds the most recent date and uses the key of the most recent date for the translation
  const dateArr = Object.entries(dates)
    .filter(([, value]) => value)
    .map(([key, value]) => [key, new Date(value)]);
  const mostRecentDate = dateArr.sort((dateA, dateB) => dateB[1] - dateA[1])[0];

  return mostRecentDate ? (
    <Typography
      variant="caption"
      color="textSecondary"
      data-qa="validate-order-details-last-adjusted"
    >
      {t(`lastUpdatedLabel.${mostRecentDate[0]}`, {
        date: formatDate(mostRecentDate[1])
      })}
    </Typography>
  ) : null;
};

const InfoItem = ({ label, value }) => (
  <Box
    display="flex"
    flexDirection={{ xs: "column", sm: "row" }}
    justifyContent="space-between"
    pb={{ xs: 1.5, sm: 0 }}
  >
    <Typography variant="body2" noWrap sx={{ pr: 1, flex: "0 1 40%" }}>
      {label}
    </Typography>
    <Typography variant="subtitle2" sx={{ pr: { xs: 0, md: 3 }, flex: 1 }}>
      {value ? value : "-"}
    </Typography>
  </Box>
);

const ValidateOrderDetails = ({
  order,
  updateOrder,
  isUpdating,
  showFinalReviewBtn
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const t = useScopedTranslation("validate.order.details");
  const { t: globalT } = useTranslation();

  const [scoreAdjusted, setScoreAdjusted] = useState(order.scoreAdjusted);
  const [adjustedEmployee, setAdjustedEmployee] = useState(
    order.adjustedEmployee || null
  );
  const [surveyComments, setSurveyComments] = useState(
    order.surveyComments || ""
  );

  const getSurveyEmployee = () => {
    //TODO: Get survey employee by user_id
    // if (order.surveyEmployees.length) {
    //   return order.surveyEmployees[order.surveyEmployees.length - 1].fullName;
    // }
    if (order.surveyEmployee) return order.surveyEmployee;
  };

  const isSurvey = () => !!order.score;
  const isALaCarte = () => order.orderType === validateOrderTypes.aLaCarte;
  const isStandardOrder = () => order.orderType === validateOrderTypes.standard;
  const showTargetedEmployee = () =>
    isALaCarte() &&
    (order.status === validateOrderStatuses.ordered ||
      order.status === validateOrderStatuses.inProgress);
  const showOrderNotes = () =>
    isALaCarte() &&
    (order.status === validateOrderStatuses.ordered ||
      order.status === validateOrderStatuses.inProgress);

  const getDetailsFields = t =>
    [
      { name: "type", value: order.shopType.description },
      {
        name: "programDate",
        value: order.timeRange,
        conditions: [isStandardOrder]
      },
      { name: "status", value: t("validate.statuses." + order.status) },
      {
        name: "orderedAt",
        value: order.createdAt,
        conditions: [isALaCarte]
      },
      {
        name: "orderedBy",
        value: order.orderedBy && order.orderedBy.fullName,
        conditions: [isALaCarte],
        note:
          !(order.orderedBy && order.orderedBy.fullName) &&
          t("validate.orderedByForLegacy")
      },
      { name: "location", value: order.location.name },
      {
        name: "targetedEmployee",
        value: order.employee && order.employee.fullName,
        conditions: [showTargetedEmployee]
      },
      {
        name: "orderNotes",
        value: order.comments,
        conditions: [showOrderNotes]
      }
    ].filter(
      field =>
        !field.conditions || !field.conditions.find(condition => !condition())
    );

  const getSurveyFields = t => [
    {
      name: "employeeName",
      value: getSurveyEmployee()
    },
    {
      name: "completedAt",
      value: order.completedAt
    },
    { name: "score", value: order.score !== null ? order.score + "%" : "-" }
  ];

  const orderItems = getDetailsFields(globalT).map(item => (
    <InfoItem
      label={t(`${item.name}.label`)}
      value={item.value || "-"}
      key={item.name}
    />
  ));

  const surveyItems = getSurveyFields(globalT).map(item => (
    <InfoItem
      label={t(`${item.name}.label`)}
      value={item.value || "-"}
      key={item.name}
    />
  ));

  const handleSave = () => {
    updateOrder({
      type: "Validate::Order",
      id: order.id,
      score_adjusted: scoreAdjusted,
      ...{ employee_id_adjusted: adjustedEmployee && adjustedEmployee.id },
      surveyComments
    }).then(res => {
      if (res && res.data) {
        dispatch(
          enqueueSuccessSnackbar({
            message: t(
              res.reassigned
                ? "updateEmployeeSuccessMessage"
                : "updateSuccessMessage"
            )
          })
        );
      } else {
        dispatch(enqueueErrorSnackbar({ message: t("updateErrorMessage") }));
      }
    });
  };

  const handleSendToSupervisor = () => {
    updateOrder({
      type: "Validate::Order",
      id: order.id,
      status: "ready_for_supervisor_review"
    });
  };

  const isReadyForReview =
    order.status === validateOrderStatuses.readyForReview ||
    order.status === validateOrderStatuses.readyForSupervisorReview;
  const isOrderCompleted = order.status === validateOrderStatuses.completed;
  const isReadyForReviewOrCompleted = isReadyForReview || isOrderCompleted;
  const completedWithoutEmployee =
    isOrderCompleted && !adjustedEmployee && !order.surveyEmployee;

  const downloadSurveyBtn = (
    <Box display="flex" alignItems="center">
      <Button
        color="primary"
        disabled={!order.surveyUrl || !isReadyForReviewOrCompleted}
        onClick={() =>
          window.open(prependHttps(order.surveyUrl, true), "_blank")
        }
        startIcon={<Description />}
      >
        {t("downloadSurvey")}
      </Button>

      {(!order.surveyUrl || !isReadyForReviewOrCompleted) && (
        <InfoOutlined color="primary" />
      )}
    </Box>
  );

  return (
    <Box mt={3} display="flex" flexDirection="column" flexGrow="1">
      <Box
        display="flex"
        alignItems="center"
        bgcolor={theme.palette.secondary[50]}
        flexWrap="wrap"
        mb={2}
      >
        <Box display="flex" justifyContent="center" flex={1} p={4}>
          <GenericAvatar
            icon={order.shopType.description}
            iconSize={64}
            defaultIcon="computer"
          />
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          px={4}
          py={2}
          flex={9999}
          textAlign={{ xs: "center", md: "left" }}
        >
          <Typography variant="h3" sx={{ pb: 0.75 }}>
            {`${globalT("validate.order.routeTitle")} ${order.id}`}
          </Typography>
          <Typography
            variant="subtitle1"
            color="textSecondary"
            sx={{ pb: 0.75 }}
          >
            {`${t("status.label")}: ${globalT(
              "validate.statuses." + order.status
            )}`}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            <LastUpdatedLabel
              surveyCompletedAt={order.completedAt}
              createdAt={order.createdAt}
              scoreAdjustedAt={order.scoreAdjustedAt}
              commentsAdjustedAt={order.surveyCommentsAdjustedAt}
              employeeAdjustedAt={order.employeeIdAdjustedAt}
            />
          </Typography>
        </Box>
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        flex={1}
        bgcolor={theme.palette.secondary[50]}
        py={{ xs: 2, sm: 4 }}
        px={{ xs: 2, sm: 5 }}
      >
        <Typography variant="h6" sx={{ pb: 2 }}>
          {t("orderHeader")}
        </Typography>
        <Box display="flex" flex={1} flexWrap="wrap">
          <Box display="flex" flexDirection="column" flex={1} minWidth={260}>
            {orderItems.slice(0, 4)}
          </Box>
          <Box display="flex" flexDirection="column" flex={1} minWidth={260}>
            {orderItems.slice(4)}
          </Box>
        </Box>
      </Box>
      {isSurvey && (
        <Box
          mt={2}
          display="flex"
          flexDirection="column"
          flex={1}
          bgcolor={theme.palette.secondary[50]}
          pt={{ xs: 2, sm: 4 }}
          pb={2}
          px={{ xs: 2, sm: 5 }}
        >
          <Typography variant="h6" sx={{ pb: 2 }}>
            {t("surveyHeader")}
          </Typography>
          <Box display="flex" flex={1} alignItems="flex-start" flexWrap="wrap">
            <Box
              display="flex"
              flexDirection="column"
              flex={1}
              minWidth={260}
              pb={2}
            >
              {surveyItems}
            </Box>
            <Box flex={1} ml={-2}>
              <Box display="flex" alignItems="center">
                {!order.surveyUrl || !isReadyForReviewOrCompleted ? (
                  <Tooltip
                    title={
                      <Typography color="textPrimary">
                        {t("surveyUnavailableTooltip")}
                      </Typography>
                    }
                  >
                    {downloadSurveyBtn}
                  </Tooltip>
                ) : (
                  downloadSurveyBtn
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      )}
      <ValidateOrderAdjustments
        order={order}
        onCommentsChange={setSurveyComments}
        onEmployeeChange={setAdjustedEmployee}
        onScoreChange={setScoreAdjusted}
        scoreAdjusted={scoreAdjusted}
        surveyComments={surveyComments}
        adjustedEmployee={adjustedEmployee}
        isOrderCompleted={isOrderCompleted}
        isReadyForReview={isReadyForReview}
        isUpdating={isUpdating}
      />
      <Box display="flex" justifyContent="flex-end" mt={3}>
        {order.status === validateOrderStatuses.readyForReview &&
          showFinalReviewBtn && (
            <Button
              color="primary"
              onClick={handleSendToSupervisor}
              variant="contained"
              disabled={!isReadyForReviewOrCompleted}
            >
              {t("sendToSupervisor")}
            </Button>
          )}
        <Button
          color="primary"
          onClick={handleSave}
          variant="contained"
          ml={2}
          disabled={isUpdating || completedWithoutEmployee}
        >
          {t("save")}
        </Button>
      </Box>
    </Box>
  );
};

ValidateOrderDetails.propTypes = {
  order: PropTypes.object.isRequired,
  updateOrder: PropTypes.func.isRequired,
  isUpdating: PropTypes.bool.isRequired,
  showFinalReviewBtn: PropTypes.bool.isRequired
};

export default ValidateOrderDetails;
